import { useState } from 'react'
import { Wrench } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { resourceProfilesApi, type ResourceProfile } from '@/api/resourceCenterApi'
import { useSupportActivityTypes } from '@/hooks/useResourceCenter'
import { todayIso } from '@/lib/resourceCalendar'
import { DateInput, ErrorLine, Field, useProfileWrite } from './shared'

/**
 * "+ Add support activity" (slide 13): picked from the center list, hours
 * per working day, reserved before PR / RV work.
 */
export function SupportActivityDialog({
  profile,
  open,
  onOpenChange,
  initialDate,
}: {
  profile: ResourceProfile
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-fill a single day, e.g. when opened from that day on the calendar. */
  initialDate?: string
}) {
  const types = useSupportActivityTypes()
  const active = (types.data ?? []).filter(t => t.active)
  const [typeId, setTypeId] = useState('')
  const [hours, setHours] = useState('')
  const [from, setFrom] = useState(initialDate ?? todayIso())
  // From a calendar day: that day only. Otherwise open-ended, as before.
  const [to, setTo] = useState(initialDate ?? '')
  const write = useProfileWrite()

  const pick = (id: string) => {
    setTypeId(id)
    const t = active.find(x => x.id === id)
    if (t) setHours(String(t.defaultHoursPerDay))
  }

  const reset = () => {
    setTypeId('')
    setHours('')
    setTo('')
  }
  const handleOpenChange = (o: boolean) => {
    onOpenChange(o)
    if (!o) reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Wrench className='h-4 w-4' />
            </span>
            Add support activity
          </DialogTitle>
          <DialogDescription>Reserved first, before PR / RV work, for {profile.name}.</DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='grid grid-cols-[1fr_5rem] gap-2'>
            <Field label='Activity'>
              <Select value={typeId} onValueChange={pick}>
                <SelectTrigger className='h-9 text-sm' aria-label='Support activity'>
                  <SelectValue placeholder={active.length ? 'Choose' : 'None on the center list'} />
                </SelectTrigger>
                <SelectContent>
                  {active.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.code} · {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label='h / day'>
              <Input
                type='number'
                min={0.25}
                max={24}
                step={0.25}
                value={hours}
                onChange={e => setHours(e.target.value)}
                className='h-9 text-sm tabular-nums'
              />
            </Field>
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <Field label='From'>
              <DateInput
                value={from}
                onChange={v => {
                  setFrom(v)
                  // A From moved past a set To drags it along (as the absence
                  // form does); an empty To stays open-ended.
                  if (to && to < v) setTo(v)
                }}
                ariaLabel='Support from'
              />
            </Field>
            <Field label='To (optional)'>
              <DateInput value={to} onChange={setTo} min={from} ariaLabel='Support to' />
            </Field>
          </div>
          <ErrorLine message={write.error} />
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !typeId ||
              !from ||
              !(Number(hours) > 0) ||
              (!!to && to < from) ||
              write.pending
            }
            onClick={() =>
              write.run(
                () =>
                  resourceProfilesApi.addSupportActivity(profile.userId, {
                    supportActivityTypeId: typeId,
                    hoursPerDay: Number(hours),
                    fromDate: from,
                    toDate: to || null,
                  }),
                () => handleOpenChange(false),
              )
            }
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
